import {
  Button,
  Typography,
  createStyles,
  makeStyles,
  Theme,
  Grid,
  Dialog,
  DialogContent,
  DialogActions,
  DialogContentText,
  Link,
  Divider,
  DialogTitle,
  Paper,
  IconButton,
  Radio,
  FormControl,
  RadioGroup,
  FormControlLabel,
  useMediaQuery,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
} from '@material-ui/core';
import React, { useState, FC, useEffect } from 'react';
import {
  PayerPaymentMethod,
  PaymentMethodStatus,
  PaymentRequestInfo,
  PaymentMethodType,
  CurrencyType,
  PaymentMethodHolder,
  Person,
  AcceptedPaymentMethod,
  MerchantInfo,
} from '../gql-types.generated';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ErrorIcon from '@material-ui/icons/Error';
import AddIcon from '@material-ui/icons/Add';
import { getPaymentMethodIcon, getPaymentMethodText } from './PaymentMethods';
import { AllAcceptedPaymentMethodForDisplay } from '../util/AcceptedPaymentMethodForDisplay';
import { PaymentsDueRowData } from '../custom-types/PaymentsDueRowData';
import { useHistory } from 'react-router';
import LoadingMask from './LoadingMask';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    padding: {
      padding: theme.spacing(1, 2),
    },
    paddingSides: {
      padding: theme.spacing(0, 1),
    },
    paddingPaymentMethod: {
      padding: theme.spacing(1, 2, 0, 2),
    },
    paddingRadio: {
      padding: theme.spacing(0, 2, 1, 2),
    },
    marginTop: {
      marginTop: theme.spacing(2),
    },
    refundPolicyLink: {
      fontSize: 14,
      fontWeight: 400,
      cursor: 'pointer',
    },
    refundPolicyContent: {
      marginTop: 14,
      padding: 8,
      borderRadius: 4,
      borderWidth: 1,
      borderColor: 'lightgrey',
      borderStyle: 'solid',
    },
    invoiceLink: {
      fontSize: 14,
      fontWeight: 400,
      cursor: 'pointer',
    },
    invoiceLinkWrap: {
      width: '100%',
      textAlign: 'right',
      display: 'inline-block',
    },
    refundContent: {
      padding: theme.spacing(2, 3),
    },
    refundTitle: {
      padding: theme.spacing(2),
    },
    refundActions: {
      padding: theme.spacing(2),
    },
    closeRefundPolicyButton: {
      width: '100%',
    },
    invoiceDownload: {
      fontSize: '1rem',
    },
    total: {
      paddingLeft: theme.spacing(1),
      fontWeight: 'lighter',
    },
    amount: {
      fontWeight: 400,
      fontSize: 18,
    },
    paymentButton: {
      width: '100%',
    },
    errorBanner: {
      backgroundColor: '#FFF7F7',
      padding: theme.spacing(1, 0),
    },
    paymentError: {
      backgroundColor: '#FFF7F7',
      padding: theme.spacing(1),
    },
    hiddenElement: {
      visibility: 'hidden',
    },
    referenceText: {
      padding: theme.spacing(1, 2),
      wordBreak: 'break-all',
    },
    cancelPadding: {
      paddingRight: theme.spacing(1.5),
    },
    submitPadding: {
      paddingTop: theme.spacing(1.5),
    },
    namePadding: {
      padding: theme.spacing(0, 2),
    },
    refundPolicy: {
      textAlign: 'right',
      verticalAlign: 'middle',
    },
    totalAmount: {
      padding: theme.spacing(1, 2),
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'flex-end',
    },
    actionPadding: {
      padding: theme.spacing(0.5, 2, 2, 2),
    },
    headerCell: {
      backgroundColor: '#FFFFFF',
      color: 'grey',
    },
    tableContainer: {
      maxHeight: '350px',
    },
    grid: {
      fontSize: '16px',
    },
    gridText: {
      width: '50%',
      fontWeight: 'bold',
    },
    gridInvoice: {
      width: '50%',
      textAlign: 'right',
    },
    paymentSummaryPadding: {
      padding: theme.spacing(2, 2, 1, 2),
    },
    referenceTextMobile: {
      wordBreak: 'break-all',
      wordWrap: 'break-word',
    },
    subTotalText: {
      textAlign: 'right',
      fontSize: '16px',
      fontWeight: 'bold',
    },
    subTotalAmount: {
      textAlign: 'right',
      fontSize: '16px',
    },
    subTotalContainer: {
      paddingTop: theme.spacing(1),
    },
    checkboxIcon: {
      alignItems: 'flex-start',
      margin: 0,
      padding: 0,
    },
    checkboxLabel: {
      paddingLeft: theme.spacing(1),
    },
    checkboxRoot: {
      alignItems: 'flex-start',
      marginLeft: 0,
    },
  }),
);

interface PayPaymentRequestProps {
  merchantInfo?: MerchantInfo;
  allowCreditCard: boolean;
  allowECheck: boolean;
  changePaymentMethod: (paymentMethod: PayerPaymentMethod | undefined) => void;
  continueWithCredit?: () => void;
  hasPaymentMethods?: boolean;
  paymentMethod?: PayerPaymentMethod | null;
  paymentRequestInfo?: PaymentRequestInfo;
  paymentRequestInfoList?: PaymentsDueRowData[];
  submitPayment?: () => void;
  displayRefundPolicy: boolean;
  toggleRefundPolicyDisplay: () => void;
  viewerUser?: Person;
  paymentMethods?: PayerPaymentMethod[] | null;
  sortedPaymentMethods?: PayerPaymentMethod[] | null;
  billingAddresses?: PaymentMethodHolder[] | null;
  handleAddCard: (isLoading?: boolean) => void;
  handleAddBank: () => void;
  defaultPaymentMethod?: PayerPaymentMethod | null;
  paymentError?: Error;
  isCreditCardAccepted: (
    paymentMethod: PayerPaymentMethod | undefined,
    acceptedPaymentMethods?: AcceptedPaymentMethod[] | null,
  ) => boolean;
  loading: boolean;
  paymentAddVisible?: boolean;
  paymentMethodAdded?: boolean;
  cancelPayment?: (merchant?: string) => void;
  isMobileView: boolean;
  getPaymentRequestInfoAmount?: () => number;
  loadingCreditMemoBalance: boolean;
  creditMemoBalance: number;
  creditMemoBalanceError?: Error;
  fetchCreditMemoBalance: (tenantId?: string) => void;
  setMerchantInfo: (tenantId?: string) => void;
  useCreditMemo: boolean;
  setUseCreditMemo: (useCredtMemo: boolean) => void;
  sendingPayment: boolean;
  handleCreditMemoCardClose: () => void;
}

const PayPaymentRequest: FC<PayPaymentRequestProps> = props => {
  const classes = useStyles();
  const {
    merchantInfo,
    allowCreditCard,
    allowECheck,
    paymentMethod,
    paymentRequestInfo,
    paymentRequestInfoList,
    submitPayment,
    displayRefundPolicy,
    toggleRefundPolicyDisplay,
    viewerUser,
    changePaymentMethod,
    paymentMethods,
    handleAddCard,
    handleAddBank,
    defaultPaymentMethod,
    paymentError,
    isCreditCardAccepted,
    loading,
    paymentAddVisible,
    paymentMethodAdded,
    cancelPayment,
    isMobileView,
    getPaymentRequestInfoAmount,
    loadingCreditMemoBalance,
    creditMemoBalance,
    creditMemoBalanceError,
    fetchCreditMemoBalance,
    setMerchantInfo,
    useCreditMemo,
    setUseCreditMemo,
    sendingPayment,
    handleCreditMemoCardClose,
    sortedPaymentMethods,
  } = props;
  const currency = (paymentRequestInfo?.currency as CurrencyType) || (merchantInfo?.defaultCurrency as CurrencyType) || 'USD';
  const acceptedPaymentMethods = paymentRequestInfo?.acceptedPaymentMethods || merchantInfo?.acceptedPaymentMethods;

  const history = useHistory();

  if (window.location.pathname === '/make-payment' && !paymentRequestInfoList) {
    history.push('/');
    return <></>;
  }

  let defaultAllowed =
    defaultPaymentMethod &&
    ((defaultPaymentMethod.paymentMethod?.type === PaymentMethodType.CreditCard &&
      allowCreditCard &&
      isCreditCardAccepted(defaultPaymentMethod, acceptedPaymentMethods)) ||
      (defaultPaymentMethod.paymentMethod?.type === PaymentMethodType.PaymentBankUs && allowECheck));

  const [methodsExpanded, setMethodsExpanded] = useState<boolean>(!defaultAllowed);
  const [selectedRadio, setSelectedRadio] = useState<string | undefined>(
    defaultAllowed ? defaultPaymentMethod?.paymentMethod?.id : paymentMethod?.paymentMethod?.id,
  );
  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  useEffect(() => {
    setSelectedRadio(undefined);
    setUseCreditMemo(false);

    return () => {
      handleCreditMemoCardClose();
    };
  }, []);

  useEffect(() => {
    if (
      loading === false &&
      paymentAddVisible === false &&
      paymentMethodAdded === false &&
      ((paymentMethods && paymentMethods.length === 0) || !paymentMethods)
    ) {
      handleAddCard();
    }
  }, [loading, paymentRequestInfoList]);

  useEffect(() => {
    changePaymentMethod(undefined);

    defaultAllowed =
      defaultPaymentMethod &&
      ((defaultPaymentMethod.paymentMethod?.type === PaymentMethodType.CreditCard &&
        allowCreditCard &&
        isCreditCardAccepted(defaultPaymentMethod, acceptedPaymentMethods)) ||
        (defaultPaymentMethod.paymentMethod?.type === PaymentMethodType.PaymentBankUs && allowECheck));
    //If the default payment method type is supported by the payment request, set the selected payment method to the default payment method
    if (defaultPaymentMethod && defaultAllowed && changePaymentMethod) {
      changePaymentMethod(defaultPaymentMethod);
      setMethodsExpanded(false);
      setSelectedRadio(defaultPaymentMethod?.paymentMethod?.id);
    }
  }, [defaultPaymentMethod]);

  useEffect(() => {
    if (paymentAddVisible === true && paymentMethods && paymentMethods.length > 0) {
      handleAddCard(true);
    }

    if (paymentMethods?.length === 1 && !defaultPaymentMethod) {
      changePaymentMethod(undefined);

      const [firstPaymentMethod] = paymentMethods;
      const methodAllowed =
        firstPaymentMethod &&
        ((firstPaymentMethod.paymentMethod?.type === PaymentMethodType.CreditCard &&
          allowCreditCard &&
          isCreditCardAccepted(firstPaymentMethod, acceptedPaymentMethods)) ||
          (firstPaymentMethod.paymentMethod?.type === PaymentMethodType.PaymentBankUs && allowECheck));
      //Selecting when only one payment method is present
      //If the first payment method type is supported by the payment request, set the selected payment method to the first payment method
      if (methodAllowed && changePaymentMethod) {
        changePaymentMethod(firstPaymentMethod);
        setMethodsExpanded(false);
        setSelectedRadio(firstPaymentMethod?.paymentMethod?.id);
      }
    }

    if (paymentMethods?.length === 0) {
      changePaymentMethod(undefined);
      setMethodsExpanded(true);
      setSelectedRadio(undefined);
    }
  }, [paymentMethods]);

  useEffect(() => {
    if (paymentRequestInfo && !merchantInfo) {
      setMerchantInfo(paymentRequestInfo.tenantId);
    }
  }, [paymentRequestInfo]);

  useEffect(() => {
    if (merchantInfo?.features?.creditMemos.enabled) {
      fetchCreditMemoBalance(merchantInfo?.owner?.tenantId);
    }
  }, [merchantInfo]);

  const toggleMethodsExpanded = () => {
    setMethodsExpanded(!methodsExpanded);
  };

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const MAX_CARDS = 5;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const MAX_BANKS = 1;
  let cardAmount = 0;
  let bankAmount = 0;
  paymentMethods?.forEach(method => {
    if (method.paymentMethod?.type === PaymentMethodType.CreditCard) {
      cardAmount += 1;
    }
    if (method.paymentMethod?.type === PaymentMethodType.PaymentBankUs) {
      bankAmount += 1;
    }
  });

  const billingInfo =
    paymentMethod?.paymentMethod?.type === PaymentMethodType.CreditCard
      ? paymentMethod?.paymentMethod?.creditCard?.cardHolder?.address
      : paymentMethod?.paymentMethod?.paymentBank?.accountHolder?.address;

  const handleSelectPaymentMethod = (paymentMethod: PayerPaymentMethod | undefined) => {
    if (changePaymentMethod && paymentMethod) {
      changePaymentMethod(paymentMethod);
    }
  };

  const handleSelectRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRadio(event.target.value);
    if (paymentMethods) {
      const paymentMethod = paymentMethods.find(method => {
        return method.paymentMethod?.id && method.paymentMethod.id.localeCompare(event.target.value) === 0;
      });
      handleSelectPaymentMethod(paymentMethod);
    }
    toggleMethodsExpanded();
  };

  const getSubTotalAmount = () => {
    if (paymentRequestInfo) {
      return getPaymentRequestInfoAmount ? getPaymentRequestInfoAmount() : paymentRequestInfo.totalDue;
    }

    if (paymentRequestInfoList) {
      let totalAmount = 0;
      paymentRequestInfoList.forEach(paymentRequest => {
        totalAmount += paymentRequest.amount || 0;
      });
      return totalAmount;
    }
    return 0;
  };

  const getCreditAmount = () => {
    if (!creditMemoBalanceError && creditMemoBalance > 0 && useCreditMemo && merchantInfo?.features?.creditMemos.enabled) {
      const subTotalAmount = getSubTotalAmount();
      const creditAmount = creditMemoBalance > subTotalAmount ? subTotalAmount : creditMemoBalance;

      return creditAmount;
    }
    return 0;
  };

  const getTotalAmount = () => {
    const subTotalAmount = getSubTotalAmount();
    const creditAmount = getCreditAmount();

    return subTotalAmount - creditAmount;
  };

  const toggleUseCreditMemo = () => {
    setUseCreditMemo(!useCreditMemo);
  };

  const methodsRadio = (
    <FormControl>
      <RadioGroup name="methods" value={selectedRadio} onChange={handleSelectRadio}>
        {sortedPaymentMethods?.map(method => {
          const isDisabled =
            method.paymentMethod?.type === PaymentMethodType.CreditCard
              ? !isCreditCardAccepted(method, acceptedPaymentMethods)
              : !allowECheck;
          return (
            <FormControlLabel
              key={method?.paymentMethod?.id}
              value={method.paymentMethod?.id}
              control={<Radio color="primary" className={isDisabled ? classes.hiddenElement : ''} />}
              disabled={isDisabled}
              label={
                <Grid item container xs={12} alignItems="center">
                  {getPaymentMethodIcon(method, isDisabled, 48)}
                  <Typography>{getPaymentMethodText(method)}</Typography>
                </Grid>
              }
            />
          );
        })}
      </RadioGroup>
      {bankAmount < MAX_BANKS && allowECheck && (
        <Grid item container xs={12}>
          <Button
            size="small"
            variant="text"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleAddBank}
            data-cy="add-bank-account"
          >
            ADD BANK ACCOUNT
          </Button>
        </Grid>
      )}
      {cardAmount < MAX_CARDS && allowCreditCard && (
        <Grid item container xs={12}>
          <Button
            size="small"
            variant="text"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              handleAddCard();
            }}
            data-cy="add-credit-card"
          >
            ADD CREDIT CARD
          </Button>
        </Grid>
      )}
    </FormControl>
  );

  const cancelOrSubmitPayment = () => {
    const canSubmitPayment =
      submitPayment &&
      (paymentRequestInfo || paymentRequestInfoList) &&
      paymentMethod &&
      paymentMethod.paymentMethod?.status !== PaymentMethodStatus.Unknown &&
      paymentMethod.paymentMethod?.status !== PaymentMethodStatus.ProcessingError;
    return (
      <>
        <Grid xs={12} lg={6} className={matches ? '' : classes.cancelPadding}>
          <Button
            className={classes.paymentButton}
            color="primary"
            variant="outlined"
            data-cy="cancel-payment-button"
            onClick={() => {
              if (cancelPayment) cancelPayment(merchantInfo?.owner?.tenantId);
            }}
          >
            Cancel
          </Button>
        </Grid>
        <Grid item xs={12} lg={6} className={matches ? classes.submitPadding : ''}>
          <Button
            className={classes.paymentButton}
            color="primary"
            variant="contained"
            data-cy="submit-payment-button"
            disabled={!canSubmitPayment}
            onClick={() => {
              if (submitPayment) submitPayment();
            }}
          >
            Submit Payment
          </Button>
        </Grid>
      </>
    );
  };

  return (
    <>
      <LoadingMask loading={sendingPayment} />
      <Dialog open={displayRefundPolicy}>
        <DialogTitle className={classes.refundTitle}>
          {paymentRequestInfo?.merchantName || merchantInfo?.name} {'Refund Policy'}
        </DialogTitle>
        <DialogContent className={classes.refundContent}>
          <DialogContentText color={'textPrimary'}>{paymentRequestInfo?.refundPolicy || merchantInfo?.refundPolicy}</DialogContentText>
        </DialogContent>
        <DialogActions className={classes.refundActions}>
          <Button
            className={classes.closeRefundPolicyButton}
            color={'primary'}
            variant={'contained'}
            onClick={() => {
              toggleRefundPolicyDisplay();
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container xs={12} spacing={2} direction="column">
        <Paper>
          {paymentError && (
            <Grid item container xs={12} className={classes.paymentError}>
              <Grid item container xs={1} justify="center" alignItems="center">
                <ErrorIcon fontSize="small" color="error" />
              </Grid>
              <Grid item container xs={11}>
                <Typography variant="subtitle2" color="error">
                  {`Payment failed. ${
                    paymentError.message.length > 0 ? paymentError.message : 'Please verify your information and try again.'
                  }`}
                </Typography>
              </Grid>
            </Grid>
          )}
          <Grid item container xs={12} className={classes.padding}>
            <Typography variant="h6">Payment Information</Typography>
          </Grid>
          <Grid item container xs={12} direction="column" className={classes.padding}>
            <Typography variant="body2" color="textSecondary">
              Email
            </Typography>
            <Typography variant="body1">{viewerUser?.email}</Typography>
          </Grid>

          {/* credit memo */}
          {merchantInfo?.features?.creditMemos.enabled && (
            <Grid item container xs={12} style={{ position: 'relative' }} className={classes.padding}>
              <LoadingMask loading={loadingCreditMemoBalance} />
              <Typography variant="body2" color="textSecondary">
                Credit Memos
              </Typography>
              {(creditMemoBalanceError || creditMemoBalance === 0) && (
                <Grid item xs={12}>
                  No Credit Balance available
                </Grid>
              )}
              {!creditMemoBalanceError && creditMemoBalance > 0 && (
                <Grid item xs={12}>
                  <FormControlLabel
                    classes={{ label: classes.checkboxLabel, root: classes.checkboxRoot }}
                    control={
                      <Checkbox
                        color="primary"
                        checked={useCreditMemo}
                        onClick={toggleUseCreditMemo}
                        className={classes.checkboxIcon}
                      />
                    }
                    label={
                      <>
                        <Typography>Apply Credit to balance</Typography>
                        <Typography>
                          {typeof creditMemoBalance === 'number'
                            ? new Intl.NumberFormat('en', {
                                style: 'currency',
                                currency: currency || 'USD',
                                currencyDisplay: 'symbol',
                              }).format(loadingCreditMemoBalance ? 0 : creditMemoBalance / 100)
                            : null}
                        </Typography>
                      </>
                    }
                  />
                </Grid>
              )}
            </Grid>
          )}

          {/* payment method */}
          <Grid item container xs={12}>
            {!paymentMethod && (
              <Grid item container xs={12} className={classes.errorBanner}>
                <Grid item container xs={1} justify="center" alignItems="center">
                  <ErrorIcon fontSize="small" color="error" />
                </Grid>
                <Grid item container xs={11}>
                  <Typography color="error">{`Please select a payment method.`}</Typography>
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid
            item
            container
            xs={12}
            justify="space-between"
            alignItems="center"
            direction="row"
            className={classes.paddingPaymentMethod}
          >
            <Grid item container xs={8}>
              <Typography variant="body2" color="textSecondary">
                Payment Method
              </Typography>
            </Grid>
            <Grid item container xs={4} justify="flex-end">
              <IconButton color="primary" onClick={toggleMethodsExpanded} size="small" data-cy="payment-method-list-icon">
                {methodsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Grid>
            {acceptedPaymentMethods &&
              (acceptedPaymentMethods.length < 7 ||
                (acceptedPaymentMethods.length === 7 && acceptedPaymentMethods.indexOf(AcceptedPaymentMethod.Unknown) !== -1)) && (
                <Grid item container xs={12}>
                  <Typography>{`Some payment methods are not supported in this region. Please use a supported payment method (${acceptedPaymentMethods
                    .map(method => {
                      return AllAcceptedPaymentMethodForDisplay.find(data => data.key === method)?.value;
                    })
                    .join(', ')}).`}</Typography>
                </Grid>
              )}
          </Grid>
          {!methodsExpanded && isCreditCardAccepted(paymentMethod !== null ? paymentMethod : undefined, acceptedPaymentMethods) && (
            <Grid item container xs={12} className={classes.paddingSides} alignItems="center">
              {paymentMethod && getPaymentMethodIcon(paymentMethod, false, 48)}
              {paymentMethod && getPaymentMethodText(paymentMethod)}
            </Grid>
          )}
          {methodsExpanded && (
            <Grid item container xs={12} className={classes.paddingRadio}>
              {methodsRadio}
            </Grid>
          )}

          {/* billing address */}
          {billingInfo && (
            <>
              <Grid item container xs={12} direction="column" className={classes.padding}>
                <Typography variant="body2" color="textSecondary">
                  Billing Address
                </Typography>
                <Typography>{`${billingInfo?.line1} ${billingInfo?.line2 || ''}`}</Typography>
                <Typography color="textSecondary">{`${billingInfo?.postalCode}, ${billingInfo?.country}`}</Typography>
              </Grid>
            </>
          )}
        </Paper>
        <Paper className={classes.marginTop}>
          <Grid item container xs={12} direction="column">
            <Grid item xs={12}>
              <Typography variant="subtitle2" className={classes.paymentSummaryPadding}>
                PAYMENT SUMMARY
              </Typography>
            </Grid>
            <Grid item container xs={12} className={classes.namePadding}>
              <Grid item sm={7} xs={12}>
                <Typography variant="h6">{paymentRequestInfo?.merchantName || merchantInfo?.name}</Typography>
              </Grid>
              <Grid item sm={5} xs={12} className={classes.refundPolicy}>
                <Link
                  variant="body2"
                  className={classes.refundPolicyLink}
                  onClick={() => {
                    toggleRefundPolicyDisplay();
                  }}
                  hidden={!paymentRequestInfo?.refundPolicy && !merchantInfo?.refundPolicy}
                  data-cy="view-refund-policy"
                >
                  {displayRefundPolicy ? 'Hide refund policy' : 'View refund policy'}
                </Link>
              </Grid>
            </Grid>
            <Divider />
          </Grid>
          <Grid item container xs={12}>
            <TableContainer className={classes.tableContainer}>
              <Table stickyHeader>
                {!isMobileView && (
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.headerCell}>Reference #</TableCell>
                      <TableCell className={classes.headerCell}>Invoice</TableCell>
                      <TableCell className={classes.headerCell} align="right">
                        Total Due
                      </TableCell>
                      <TableCell className={classes.headerCell} align="right">
                        Payment
                      </TableCell>
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {paymentRequestInfo && isMobileView && (
                    <TableRow>
                      <TableCell>
                        <Grid container className={classes.grid}>
                          <Grid item container xs={12}>
                            <div className={classes.gridText}>Complete Payment</div>
                            <div className={classes.gridInvoice}>
                              {paymentRequestInfo.invoiceLink && (
                                <Link href={paymentRequestInfo.invoiceLink || '#'}>Download Invoice</Link>
                              )}
                            </div>
                          </Grid>
                          <Grid item xs={12} className={classes.referenceTextMobile}>
                            {paymentRequestInfo.referenceNumber}
                          </Grid>
                          <Grid item xs={12}>
                            {new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'symbol' }).format(
                              (getPaymentRequestInfoAmount ? getPaymentRequestInfoAmount() : paymentRequestInfo?.totalDue || 0) / 100,
                            )}
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  )}
                  {paymentRequestInfoList && isMobileView && (
                    <>
                      {paymentRequestInfoList?.map((paymentRequest, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Grid container className={classes.grid}>
                              <Grid item container xs={12}>
                                <div className={classes.gridText}>Complete Payment</div>
                                <div className={classes.gridInvoice}>
                                  {paymentRequest.paymentDue.paymentRequest?.invoiceLink && (
                                    <Link href={paymentRequest.paymentDue.paymentRequest?.invoiceLink || '#'}>Download Invoice</Link>
                                  )}
                                </div>
                              </Grid>
                              <Grid item xs={12} className={classes.referenceTextMobile}>
                                {paymentRequest?.paymentDue?.paymentRequest?.referenceNumber}
                              </Grid>
                              <Grid item xs={12}>
                                {new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'symbol' }).format(
                                  (paymentRequest.amount || 0) / 100,
                                )}
                              </Grid>
                            </Grid>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}

                  {/* Desktop Render */}
                  {paymentRequestInfo && !isMobileView && (
                    <TableRow>
                      <TableCell>
                        {paymentRequestInfo.referenceNumber.length > 6
                          ? `***${paymentRequestInfo.referenceNumber.slice(-6)}`
                          : paymentRequestInfo.referenceNumber}
                      </TableCell>
                      <TableCell>
                        {paymentRequestInfo.invoiceLink ? <Link href={paymentRequestInfo.invoiceLink || '#'}>Download</Link> : '-- --'}
                      </TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'symbol' }).format(
                          (getPaymentRequestInfoAmount ? getPaymentRequestInfoAmount() : paymentRequestInfo?.totalDue || 0) / 100,
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'symbol' }).format(
                          (getPaymentRequestInfoAmount ? getPaymentRequestInfoAmount() : paymentRequestInfo?.totalDue || 0) / 100,
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                  {paymentRequestInfoList && !isMobileView && (
                    <>
                      {paymentRequestInfoList?.map((paymentRequest, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {paymentRequest?.paymentDue?.paymentRequest?.referenceNumber &&
                            paymentRequest?.paymentDue?.paymentRequest?.referenceNumber.length > 6
                              ? `***${paymentRequest?.paymentDue?.paymentRequest?.referenceNumber.slice(-6)}`
                              : paymentRequest?.paymentDue?.paymentRequest?.referenceNumber}
                          </TableCell>
                          <TableCell>
                            {paymentRequest.paymentDue.paymentRequest?.invoiceLink ? (
                              <Link href={paymentRequest.paymentDue.paymentRequest?.invoiceLink || '#'}>Download</Link>
                            ) : (
                              '-- --'
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'symbol' }).format(
                              (paymentRequest.paymentDue.paymentRequest?.totalDue
                                ? paymentRequest.paymentDue.paymentRequest.totalDue - paymentRequest.discountAmount
                                : 0) / 100,
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'symbol' }).format(
                              (paymentRequest.amount || 0) / 100,
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          {merchantInfo?.features?.creditMemos.enabled && !creditMemoBalanceError && creditMemoBalance > 0 && useCreditMemo && (
            <Grid item container xs={12} className={classes.totalAmount}>
              <Grid item container xs={12} className={classes.subTotalContainer}>
                <Grid item xs={8} md={9} className={classes.subTotalText}>
                  Subtotal
                </Grid>
                <Grid item xs={4} md={3} className={classes.subTotalAmount}>
                  {new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'symbol' }).format(
                    getSubTotalAmount() / 100,
                  )}
                </Grid>
              </Grid>
              <Grid item container xs={12} className={classes.subTotalContainer}>
                <Grid item xs={8} md={9} className={classes.subTotalText}>
                  Credit Balance
                </Grid>
                <Grid item xs={4} md={3} className={classes.subTotalAmount}>
                  -
                  {new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'symbol' }).format(
                    getCreditAmount() / 100,
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
          <Grid item container xs={12} className={classes.totalAmount}>
            <Typography variant="h4" style={{ fontWeight: 'bold' }}>
              {'Total'}
            </Typography>
            <Typography variant="h3" className={classes.total} data-cy="amount">
              {new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'symbol' }).format(getTotalAmount() / 100)}
            </Typography>
          </Grid>
          <Grid item container xs={12} className={classes.actionPadding}>
            {cancelOrSubmitPayment()}
          </Grid>
        </Paper>
      </Grid>
    </>
  );
};

export default PayPaymentRequest;
